import { useState, useCallback, useRef, useEffect } from 'react';
import { MenuProvider } from './context/MenuContext';
import { CartProvider, useCart } from './context/CartContext';
import { MenuView } from './components/MenuView';
import { CartDrawer } from './components/CartDrawer';
import { CheckoutForm } from './components/CheckoutForm';
import { OrderConfirmation } from './components/OrderConfirmation';
import { OrderWaiting } from './components/OrderWaiting';
import { ClosedOverlay } from './components/ClosedOverlay';
import { InfoPage } from './components/InfoPage';
import { ReservationForm } from './components/ReservationForm';
import { useMenu } from './context/MenuContext';
import { I18nContext, createTranslator, useI18n, LANG_LABELS, LANG_FLAGS, ALL_LANGS, type Lang } from './i18n';

function getMode(): 'reservation' | 'order' {
  const params = new URLSearchParams(window.location.search);
  if (params.get('mode') === 'reservation') return 'reservation';
  return 'order';
}

function WidgetContent() {
  const { loading, error, business, onlineStatus, widgetTheme, minDeliveryTime, showMinDeliveryTime } = useMenu();
  const { view } = useCart();
  const { t } = useI18n();
  const mode = getMode();

  if (loading) {
    return <div className="hc-loading">{t('menu.loading')}</div>;
  }

  if (error) {
    return <div className="hc-error">{t('menu.error')} {error}</div>;
  }

  // Reservation mode — separate standalone view
  if (mode === 'reservation') {
    return (
      <div className={`hc-widget hc-theme-${widgetTheme}`}>
        <WidgetHeader />
        <main className="hc-main">
          <ReservationForm />
        </main>
      </div>
    );
  }

  // Show closed/paused overlay when not available
  if (onlineStatus && (!onlineStatus.is_open || onlineStatus.is_paused)) {
    return (
      <div className={`hc-widget hc-theme-${widgetTheme}`}>
        <WidgetHeader />
        <main className="hc-main">
          <ClosedOverlay status={onlineStatus} />
        </main>
      </div>
    );
  }

  return (
    <div className={`hc-widget hc-theme-${widgetTheme}`}>
      <WidgetHeader />
      {showMinDeliveryTime && minDeliveryTime != null && minDeliveryTime > 0 && view === 'menu' && (
        <div className="hc-delivery-notice">
          &#9202; {t('delivery.minTime', { minutes: String(minDeliveryTime) })}
        </div>
      )}
      <main className="hc-main">
        {view === 'menu' && <MenuView />}
        {view === 'cart' && <CartDrawer />}
        {view === 'checkout' && <CheckoutForm />}
        {view === 'waiting' && <OrderWaiting />}
        {view === 'confirmation' && <OrderConfirmation />}
        {view === 'info' && <InfoPage />}
      </main>
      {view === 'menu' && <CartFloatingButton />}
    </div>
  );
}

function WidgetHeader() {
  const { business } = useMenu();
  const { view, setView, totalItems } = useCart();
  const { t } = useI18n();

  return (
    <header className="hc-header">
      <h1 className="hc-business-name">{business?.name ?? 'Online Order'}</h1>
      <div className="hc-header-actions">
        <button
          className={`hc-header-btn ${view === 'info' ? 'hc-active' : ''}`}
          onClick={() => setView(view === 'info' ? 'menu' : 'info')}
          title={t('nav.info')}
        >
          &#9432;
        </button>
        <LanguageSwitcher />
        {view !== 'info' && totalItems > 0 && (
          <button
            className="hc-header-btn hc-cart-icon"
            onClick={() => setView('cart')}
            title={t('nav.cart')}
          >
            &#128722; <span className="hc-cart-badge">{totalItems}</span>
          </button>
        )}
      </div>
    </header>
  );
}

function LanguageSwitcher() {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="hc-lang-picker" ref={ref}>
      <button className="hc-header-btn hc-lang-btn" onClick={() => setOpen(!open)}>
        <span className="hc-lang-flag">{LANG_FLAGS[lang]}</span>
        <span>{LANG_LABELS[lang]}</span>
      </button>
      {open && (
        <div className="hc-lang-dropdown">
          {ALL_LANGS.map((l) => (
            <button
              key={l}
              className={`hc-lang-option ${l === lang ? 'hc-lang-active' : ''}`}
              onClick={() => { setLang(l); setOpen(false); }}
            >
              <span className="hc-lang-flag">{LANG_FLAGS[l]}</span>
              <span>{LANG_LABELS[l]}</span>
              {l === lang && <span className="hc-lang-check">&#10003;</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CartFloatingButton() {
  const { totalItems, totalPrice, setView } = useCart();
  const { t } = useI18n();
  if (totalItems === 0) return null;

  return (
    <button className="hc-cart-fab" onClick={() => setView('cart')}>
      {t('cart.fab', { count: String(totalItems), total: totalPrice.toFixed(2) })}
    </button>
  );
}

export default function App() {
  const [lang, setLang] = useState<Lang>('hr');
  const t = useCallback(createTranslator(lang), [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      <MenuProvider>
        <CartProvider>
          <WidgetContent />
        </CartProvider>
      </MenuProvider>
    </I18nContext.Provider>
  );
}
